import { useCallback } from 'react'
import { runInAction } from 'mobx'
import { useQueryClient } from 'react-query'

import { usePostPreviewStore } from 'components/PostPreviewModalV2/PostPreviewStoreProvider'
import { PostPreviewScreens } from 'components/PostPreviewModalV2/PostPreviewStore'

import { usePostFilesCreator } from './usePostFilesCreator'
import { usePostPropertyMutations } from './usePostPropertyMutations'

import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { ScheduledPostsClient } from 'api/clients/ScheduledPostsClient'
import { scheduledPostsKeys } from 'hooks/queries/scheduledPosts/scheduledPostsKeys'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'

import { ResultKind, RuntimeStatus } from '../types'
import { usePostPreviewContext } from 'components/PostPreviewModal/PostPreviewContext'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { DesignChannel } from 'components/NewFileModal/Types'

export enum PostAction {
  PublishNow = 'publish-now',
  AutoSchedule = 'auto-schedule',
  Reminder = 'reminder',
  Draft = 'draft',
  Unschedule = 'unschedule',
}

const mapActionToResultKind = (action: PostAction): ResultKind => {
  switch (action) {
    case PostAction.PublishNow:
      return 'published'
    case PostAction.AutoSchedule:
      return 'autoScheduled'
    case PostAction.Reminder:
      return 'reminderScheduled'
    case PostAction.Draft:
    case PostAction.Unschedule:
      return 'draft'
  }
}

export function usePostActionService() {
  const store = usePostPreviewStore()
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()

  const { createTargets } = usePostFilesCreator()
  const { updateStatus, updatePostAt } = usePostPropertyMutations()
  const { scheduledPost } = usePostPreviewContext()

  type TargetsReturn = Awaited<ReturnType<typeof createTargets>>
  type AccountTarget = TargetsReturn['accountTargets'][number]
  type FileTarget = TargetsReturn['fileTargets'][number]

  const bootstrapCreatingState = useCallback(
    (acct: AccountTarget[], file: FileTarget[]) => {
      const toAcct = (t: AccountTarget) => ({
        provider: t.account.provider,
        account: t.account,
        fileId: t.fileId,
        runtime: 'creating' as RuntimeStatus,
        result: null as ResultKind | null,
      })
      const toFile = (t: FileTarget) => ({
        provider: t.provider,
        file: t.file,
        fileId: t.file.id,
        runtime: 'creating' as RuntimeStatus,
        result: null as ResultKind | null,
      })
      runInAction(() => {
        store.replacePostStates([...acct.map(toAcct), ...file.map(toFile)])
      })
    },
    [store],
  )

  const pollScheduledPost = useCallback(
    async (fileId: string, scheduledPostId: number) => {
      const pause = (ms: number) => new Promise((r) => setTimeout(r, ms))

      while (true) {
        const res = await ScheduledPostsClient.show({ workspaceId, scheduledPostId })
        if (res.type !== SUCCESS_RESPONSE_TYPE) throw new Error('Polling error')

        const sp = res.data.scheduled_post
        if (sp.is_posting) {
          await pause(1000)
          continue
        }

        const ok = !sp.posting_failed
        runInAction(() =>
          store.updatePostState(fileId, {
            runtime: ok ? 'success' : 'failure',
            result: ok ? 'published' : undefined,
            postUrl: ok ? sp.post_url : undefined,
            error: ok ? undefined : 'Publish failed',
          }),
        )
        await updateStatus(fileId, ok ? PostStatusPropertyValue.Posted : PostStatusPropertyValue.Failed)

        queryClient.invalidateQueries(scheduledPostsKeys.show(scheduledPostId))
        return
      }
    },
    [workspaceId, store, updateStatus, queryClient],
  )

  const handleAccountTarget = useCallback(
    async (t: AccountTarget, action: PostAction, postAtISO: string, resKind: ResultKind) => {
      if (action === PostAction.PublishNow) {
        const id = !!t.scheduledPostId ? t.scheduledPostId : scheduledPost?.id
        if (!id) {
          runInAction(() => store.updatePostState(t.fileId, { runtime: 'failure', error: 'Missing scheduledPostId' }))
          await updateStatus(t.fileId, PostStatusPropertyValue.Failed)
          return
        }

        try {
          await ScheduledPostsClient.publish({ workspaceId, scheduledPostId: id })
          await pollScheduledPost(t.fileId, id)
        } catch (err) {
          runInAction(() =>
            store.updatePostState(t.fileId, {
              runtime: 'failure',
              error: err instanceof Error ? err.message : 'Publish failed',
            }),
          )
          await updateStatus(t.fileId, PostStatusPropertyValue.Failed)
        }

        return
      }

      const isReminder = action === PostAction.Reminder
      await updatePostAt(t.fileId, postAtISO)
      await updateStatus(
        t.fileId,
        isReminder ? PostStatusPropertyValue.ReminderScheduled : PostStatusPropertyValue.AutoPostScheduled,
      )

      runInAction(() =>
        store.updatePostState(t.fileId, {
          runtime: 'success',
          result: resKind,
          scheduledPostId: !!t.scheduledPostId ? t.scheduledPostId : scheduledPost?.id,
          postAt: postAtISO,
        }),
      )
    },
    [workspaceId, scheduledPost?.id, pollScheduledPost, store, updatePostAt, updateStatus],
  )

  const handleFileTarget = useCallback(
    async (t: FileTarget, action: PostAction, postAtISO: string, resKind: ResultKind) => {
      switch (action) {
        case PostAction.PublishNow:
          runInAction(() => store.updatePostState(t.file.id, { runtime: 'success', result: 'draft' }))
          return

        case PostAction.AutoSchedule:
          await updatePostAt(t.file.id, postAtISO)
          await updateStatus(t.file.id, PostStatusPropertyValue.ReminderScheduled)
          runInAction(() =>
            store.updatePostState(t.file.id, {
              runtime: 'success',
              result: 'reminderScheduled',
              postAt: postAtISO,
            }),
          )
          return

        case PostAction.Reminder:
          await updatePostAt(t.file.id, postAtISO)
          await updateStatus(t.file.id, PostStatusPropertyValue.ReminderScheduled)
          runInAction(() =>
            store.updatePostState(t.file.id, {
              runtime: 'success',
              result: 'reminderScheduled',
              postAt: postAtISO,
            }),
          )
          return

        case PostAction.Draft:
          runInAction(() => store.updatePostState(t.file.id, { runtime: 'success', result: resKind }))
          return
      }
    },
    [store, updatePostAt, updateStatus],
  )

  const run = useCallback(
    async (action: PostAction) => {
      if (!scheduledPost) return

      if (store.channel === DesignChannel.YoutubeShort && action === PostAction.PublishNow) {
        store.setScreen(PostPreviewScreens.YOUTUBE_SCREEN)
        return
      } else {
        store.setScreen(PostPreviewScreens.RESULT_SCREEN)
      }
      store.setLastAction(action)
      store.replacePostStates([])

      const { accountTargets, fileTargets } = await createTargets(
        scheduledPost.content || '',
        scheduledPost.source_type,
        scheduledPost.source_id,
      )
      bootstrapCreatingState(accountTargets, fileTargets)

      const postAtISO = store.postAt
      const resKind = mapActionToResultKind(action)

      await Promise.all([
        ...accountTargets.map((t) => handleAccountTarget(t, action, postAtISO, resKind)),
        ...fileTargets.map((t) => handleFileTarget(t, action, postAtISO, resKind)),
      ])

      fileTargets.forEach((t) => queryClient.invalidateQueries(['file', t.file.id]))
      store.onFilesCreated?.(fileTargets.map((t) => t.file))
    },
    [scheduledPost, createTargets, store, queryClient, bootstrapCreatingState, handleAccountTarget, handleFileTarget],
  )

  return { run }
}
